import { createReducer, on } from '@ngrx/store';
import { Vault } from '@shared/models/vault.model';
import { PaginationResponse } from '@shared/models/common.model';
import * as VaultActions from './vault.actions';
import {
  BalanceDistribution,
  RecentActivity,
  VaultWhitelistAssignment,
} from '../models/vault.model';

export interface VaultState {
  loading: boolean;
  error: string | null;
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: string | null;
  detail: Vault | null;

  balanceDistribution: BalanceDistribution | null;
  balanceDistributionLoading: boolean;
  balanceDistributionError: string | null;
  recentActivities: [] | RecentActivity[];
  recentActivitiesLoading: boolean;
  recentActivitiesError: string | null;
  recentActivitiesPagination: PaginationResponse | null;

  vaultWhitelistAssignments: VaultWhitelistAssignment[];
  vaultWhitelistAssignmentsLoading: boolean;
  vaultWhitelistAssignmentsError: string | null;
  vaultWhitelistAssignmentsPagination: PaginationResponse | null;
}

export const initialState: VaultState = {
  loading: false,
  error: null,
  operationLoading: false,
  operationError: null,
  operationSuccess: null,
  detail: null,
  balanceDistribution: null,
  balanceDistributionLoading: false,
  balanceDistributionError: null,
  recentActivities: [],
  recentActivitiesLoading: false,
  recentActivitiesError: null,
  recentActivitiesPagination: null,

  vaultWhitelistAssignments: [],
  vaultWhitelistAssignmentsLoading: false,
  vaultWhitelistAssignmentsError: null,
  vaultWhitelistAssignmentsPagination: null,
};

export const vaultReducer = createReducer(
  initialState,

  // Create
  on(VaultActions.createVault, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(VaultActions.createVaultSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(VaultActions.createVaultFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Update
  on(VaultActions.updateVault, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(VaultActions.updateVaultSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(VaultActions.updateVaultFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Clear operation success
  on(VaultActions.clearOperationSuccess, state => ({
    ...state,
    operationSuccess: null,
  })),

  // Reset
  on(VaultActions.resetState, () => initialState),
  // Load Vault Detail
  on(VaultActions.loadVaultDetail, state => ({
    ...state,
    loading: true,
    error: null,
  })),

  // Load Vault Detail Success
  on(VaultActions.loadVaultDetailSuccess, (state, { detail }) => ({
    ...state,
    detail,
    loading: false,
    error: null,
  })),

  // Load Vault Detail Failure
  on(VaultActions.loadVaultDetailFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Clear Vault Detail
  on(VaultActions.clearVaultDetail, () => initialState),

  // Load Balance Distribution
  on(VaultActions.loadBalanceDistribution, state => ({
    ...state,
    balanceDistributionLoading: true,
    balanceDistributionError: null,
  })),

  // Load Balance Distribution Success
  on(VaultActions.loadBalanceDistributionSuccess, (state, { data }) => ({
    ...state,
    balanceDistribution: data,
    balanceDistributionLoading: false,
    balanceDistributionError: null,
  })),

  // Load Balance Distribution Failure
  on(VaultActions.loadBalanceDistributionFailure, (state, { error }) => ({
    ...state,
    balanceDistributionLoading: false,
    balanceDistributionError: error,
  })),

  // Load Recent Activities
  on(VaultActions.loadRecentActivities, state => ({
    ...state,
    recentActivitiesLoading: true,
    recentActivitiesError: null,
  })),

  // Load Recent Activities Success
  on(
    VaultActions.loadRecentActivitiesSuccess,
    (state, { activities, pagination }) => ({
      ...state,
      recentActivities: activities,
      recentActivitiesPagination: pagination,
      recentActivitiesLoading: false,
      recentActivitiesError: null,
    })
  ),

  // Load Recent Activities Failure
  on(VaultActions.loadRecentActivitiesFailure, (state, { error }) => ({
    ...state,
    recentActivitiesLoading: false,
    recentActivitiesError: error,
  })),

  on(VaultActions.loadVaultWhitelistAssignments, state => ({
    ...state,
    vaultWhitelistAssignmentsLoading: true,
    vaultWhitelistAssignmentsError: null,
  })),

  on(
    VaultActions.loadVaultWhitelistAssignmentsSuccess,
    (state, { assignments, pagination }) => ({
      ...state,
      vaultWhitelistAssignments: assignments,
      vaultWhitelistAssignmentsPagination: pagination,
      vaultWhitelistAssignmentsLoading: false,
      vaultWhitelistAssignmentsError: null,
    })
  ),

  on(VaultActions.loadVaultWhitelistAssignmentsFailure, (state, { error }) => ({
    ...state,
    vaultWhitelistAssignmentsLoading: false,
    vaultWhitelistAssignmentsError: error,
  })),

  // Disable Vault
  on(VaultActions.disableVault, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(VaultActions.disableVaultSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(VaultActions.disableVaultFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Enable Vault
  on(VaultActions.enableVault, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(VaultActions.enableVaultSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(VaultActions.enableVaultFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  }))
);
